import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, Plane, Car, MapPin, Calendar as CalendarIcon, Clock, User, Phone, Mail, MessageSquare, Users, PlaneTakeoff, ArrowRight, Loader2, ChevronDown, ArrowUpDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { format } from "date-fns";
import axios from "axios";
import LocationPicker from "@/components/LocationPicker";
import TurnstileWidget from "@/components/TurnstileWidget";
import { useAppConfig } from "@/hooks/useAppConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TERMINAL_ADDRESSES = {
  T1: "Terminal 1, Kempegowda International Airport Bengaluru, KIAL Rd, Devanahalli, Karnataka 560300, India",
  T2: "Terminal 2, Kempegowda International Airport Bengaluru, KIAL Rd, Devanahalli, Karnataka 560300, India",
};

// Full 24-hour pickup window in 30-min increments — 48 slots from
// "12:00 AM" through "11:30 PM". Generated rather than hardcoded so
// we can never accidentally miss a slot again. Date-relative filtering
// (today vs future) happens in getAvailableTimeSlots below.
const TIME_SLOTS = Array.from({ length: 48 }, (_, i) => {
  const h24 = Math.floor(i / 2);
  const mins = i % 2 === 0 ? "00" : "30";
  const ampm = h24 < 12 ? "AM" : "PM";
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  return `${String(h12).padStart(2, "0")}:${mins} ${ampm}`;
});

function processFare(fare) {
  if (!fare) return null;
  return {
    ...fare,
    displayTotal: fare.displayTotal || `₹${(fare.totalFare || fare.total || 0).toLocaleString("en-IN")}`,
  };
}

function getAvailableTimeSlots(selectedDate) {
  if (!selectedDate) return TIME_SLOTS;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sel = new Date(selectedDate);
  sel.setHours(0, 0, 0, 0);
  if (sel.getTime() !== today.getTime()) return TIME_SLOTS;
  const now = new Date();
  const minTime = new Date(now.getTime() + 15 * 60 * 1000);
  const minHour = minTime.getHours();
  const minMin = minTime.getMinutes();
  return TIME_SLOTS.filter(slot => {
    const match = slot.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return false;
    let h = parseInt(match[1]);
    const m = parseInt(match[2]);
    const ampm = match[3].toUpperCase();
    if (ampm === "PM" && h !== 12) h += 12;
    if (ampm === "AM" && h === 12) h = 0;
    return h > minHour || (h === minHour && m >= minMin);
  });
}

/* ═══════════════════════════════════════════════
   FARE BREAKDOWN (collapsible)
   ═══════════════════════════════════════════════ */
const FareBreakdown = ({ fareData, computedFare }) => {
  const [showBreakdown, setShowBreakdown] = useState(false);
  if (!fareData || !fareData.success) return null;
  const f = computedFare;

  return (
    <div className="bg-[rgba(249,115,22,0.06)] border border-[rgba(249,115,22,0.15)] rounded-2xl p-4 space-y-2">
      {/* Fixed Fare header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-bold tracking-[1.5px] uppercase text-[#71717a]">Fixed Fare</p>
          <span className="font-outfit font-extrabold text-sarathi-primary text-2xl" data-testid="modal-fare">
            {f.displayTotal}
          </span>
        </div>
        <div className="text-right">
          <p className="text-[#a1a1aa] text-[13px] font-medium">{fareData.distanceKm} km · {fareData.durationText}</p>
          <p className="text-[#52525b] text-[11px] mt-0.5">
            {f.allInclusive ? "All-inclusive" : f.tripType === "OUTSTATION" ? "Outstation" : "City Ride"}
          </p>
          {f.allInclusive && <p className="text-[#3f3f46] text-[10px] mt-1">Tolls + GST included</p>}
          {!f.allInclusive && f.tripType !== "OUTSTATION" && <p className="text-[#3f3f46] text-[10px] mt-1">Includes tolls + 5% GST</p>}
        </div>
      </div>

      {/* Expandable breakdown */}
      <button
        type="button"
        onClick={() => setShowBreakdown(!showBreakdown)}
        className="flex items-center gap-1.5 text-[#a1a1aa] text-xs hover:text-white transition-colors pt-1"
        data-testid="toggle-breakdown"
      >
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showBreakdown ? "rotate-180" : ""}`} />
        {showBreakdown ? "Hide breakdown" : "View fare breakdown"}
      </button>

      <AnimatePresence>
        {showBreakdown && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="border-t border-white/10 pt-2 mt-1 space-y-1.5">
              {f.allInclusive ? (
                <>
                  <div className="flex justify-between text-sm"><span className="text-[#a1a1aa]">Base service charge</span><span className="text-white">₹{(f.baseCharge || f.internalAccounting?.baseCharge || 150).toLocaleString("en-IN")}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-[#a1a1aa]">Distance ({Math.round(fareData.distanceKm)} km × ₹{f.ratePerKm || 25})</span><span className="text-white">₹{(Math.round(fareData.distanceKm) * (f.ratePerKm || 25)).toLocaleString("en-IN")}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-[#a1a1aa]">Toll (absorbed)</span><span className="text-white">₹{(f.internalAccounting?.tollTotal || 120).toLocaleString("en-IN")}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-[#a1a1aa]">GST (absorbed)</span><span className="text-white">₹{(f.internalAccounting?.gstEstimate || 0).toLocaleString("en-IN")}</span></div>
                  {f.nightSurcharge?.applies && (<div className="flex justify-between text-sm"><span className="text-[#a1a1aa]">{f.nightSurcharge.label}</span><span className="text-white">₹{f.nightSurcharge.amount.toLocaleString("en-IN")}</span></div>)}
                  <div className="flex justify-between text-sm border-t border-white/5 pt-1.5 mt-1"><span className="text-white font-medium">All-inclusive total</span><span className="text-sarathi-primary font-semibold">{f.displayTotal}</span></div>
                  <p className="text-[#3f3f46] text-[10px] mt-1">All charges included in fixed fare above</p>
                </>
              ) : f.tripType === "IN-CITY" ? (
                <>
                  {/* In-city additive — base + distance broken out, tolls itemized,
                      GST split into CGST + SGST, all visibly summing to the total. */}
                  <div className="flex justify-between text-sm"><span className="text-[#a1a1aa]">Base service charge</span><span className="text-white">₹{(f.baseCharge || 150).toLocaleString("en-IN")}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-[#a1a1aa]">Distance ({Math.round(fareData.distanceKm)} km × ₹{f.ratePerKm || 25}/km)</span><span className="text-white">₹{(Math.round(fareData.distanceKm) * (f.ratePerKm || 25)).toLocaleString("en-IN")}</span></div>
                  {f.tollBreakdown?.map((t, i) => (<div key={`t-${i}`} className="flex justify-between text-sm"><span className="text-[#a1a1aa]">{t.name}</span><span className="text-white">₹{t.amount.toLocaleString("en-IN")}</span></div>))}
                  {f.nightSurcharge?.applies && (<div className="flex justify-between text-sm"><span className="text-[#a1a1aa]">{f.nightSurcharge.label}</span><span className="text-white">₹{f.nightSurcharge.amount.toLocaleString("en-IN")}</span></div>)}
                  {(f.cgst > 0 || f.sgst > 0) && (<div className="border-t border-white/5 pt-1.5 mt-1"><div className="flex justify-between text-sm"><span className="text-[#a1a1aa]">CGST @ 2.5%</span><span className="text-white">₹{f.cgst.toLocaleString("en-IN")}</span></div><div className="flex justify-between text-sm"><span className="text-[#a1a1aa]">SGST @ 2.5%</span><span className="text-white">₹{f.sgst.toLocaleString("en-IN")}</span></div></div>)}
                  <div className="flex justify-between text-sm border-t border-white/5 pt-1.5 mt-1"><span className="text-white font-medium">Total</span><span className="text-sarathi-primary font-semibold">{f.displayTotal}</span></div>
                </>
              ) : (
                <>
                  <div className="flex justify-between text-sm"><span className="text-[#a1a1aa]">{f.tripType === "OUTSTATION" ? `Base fare (${fareData.distanceKm} km × ₹${f.ratePerKm}/km)` : `City ride (${fareData.distanceKm} km)`}</span><span className="text-white">₹{f.baseFare.toLocaleString("en-IN")}</span></div>
                  {f.tollBreakdown?.map((t, i) => (<div key={`t-${i}`} className="flex justify-between text-sm"><span className="text-[#a1a1aa]">{t.name}</span><span className="text-white">₹{t.amount.toLocaleString("en-IN")}</span></div>))}
                  {f.driverAllowance > 0 && (<div className="flex justify-between text-sm"><span className="text-[#a1a1aa]">{f.driverAllowanceLabel || "Daily driver allowance"}</span><span className="text-white">₹{f.driverAllowance.toLocaleString("en-IN")}</span></div>)}
                  {f.nightSurcharge?.applies && (<div className="flex justify-between text-sm"><span className="text-[#a1a1aa]">{f.nightSurcharge.label}</span><span className="text-white">₹{f.nightSurcharge.amount.toLocaleString("en-IN")}</span></div>)}
                  {(f.cgst > 0 || f.sgst > 0) && (<div className="border-t border-white/5 pt-1.5 mt-1"><div className="flex justify-between text-sm"><span className="text-[#a1a1aa]">CGST @ 2.5%</span><span className="text-white">₹{f.cgst.toLocaleString("en-IN")}</span></div><div className="flex justify-between text-sm"><span className="text-[#a1a1aa]">SGST @ 2.5%</span><span className="text-white">₹{f.sgst.toLocaleString("en-IN")}</span></div></div>)}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN BOOKING MODAL
   ═══════════════════════════════════════════════ */
const BookingModal = ({ isOpen, onClose, initialRideType = null, onCityRideRequested = null }) => {
  const navigate = useNavigate();
  const [rideType, setRideType] = useState(initialRideType || "");
  const [date, setDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fareData, setFareData] = useState(null);
  // fareState machine: "idle" | "calculating" | "ready" | "error"
  const [fareState, setFareState] = useState("idle");
  const [fareError, setFareError] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [airportDirection, setAirportDirection] = useState("to_airport");
  const [terminal, setTerminal] = useState("T1");
  const [showSpecialRequests, setShowSpecialRequests] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [locationPickerOpen, setLocationPickerOpen] = useState(null); // "pickup" | "drop" | null
  const fareRequestIdRef = useRef(0);

  // Turnstile — token is fetched on widget callback, reset on every fail.
  const appConfig = useAppConfig();
  const configLoaded = !!appConfig;
  const turnstileEnabled = configLoaded && appConfig?.turnstile?.enabled;
  const turnstileSiteKey = configLoaded ? appConfig?.turnstile?.site_key : null;
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  const isLocked = initialRideType !== null;

  useEffect(() => {
    if (initialRideType) setRideType(initialRideType);
    else setRideType("");
  }, [initialRideType]);

  const [formData, setFormData] = useState({
    pickup_location: "",
    drop_location: "",
    pickup_coords: null,  // { lat, lng } — from Places Autocomplete / map pin
    drop_coords: null,
    time: "",
    passengers: "1",
    passenger_name: "",
    passenger_phone: "",
    passenger_email: "",
    flight_number: "",
    special_requests: "",
  });

  const computedFare = fareData?.success && fareData.fare ? processFare(fareData.fare) : null;

  const fetchFare = useCallback(async (pickup, drop, pickupCoords, dropCoords, time) => {
    // Bump request id so any in-flight stale request is ignored
    fareRequestIdRef.current += 1;
    const requestId = fareRequestIdRef.current;
    setFareState("calculating");
    setFareData(null);
    setFareError(null);
    try {
      const payload = {
        pickup_address: pickup,
        drop_address: drop,
        pickup_time: time || "10:00 AM",
      };
      if (pickupCoords?.lat != null && pickupCoords?.lng != null) {
        payload.pickup_lat = pickupCoords.lat;
        payload.pickup_lng = pickupCoords.lng;
      }
      if (dropCoords?.lat != null && dropCoords?.lng != null) {
        payload.drop_lat = dropCoords.lat;
        payload.drop_lng = dropCoords.lng;
      }
      const resp = await axios.post(`${API}/pricing`, payload);
      // If another request has been kicked off since, drop this response
      if (requestId !== fareRequestIdRef.current) return;
      if (resp.data?.success) {
        setFareData(resp.data);
        setFareState("ready");
      } else {
        setFareData(resp.data);
        setFareError(resp.data?.error || "Unable to calculate fare.");
        setFareState("error");
      }
    } catch {
      if (requestId !== fareRequestIdRef.current) return;
      setFareError("Unable to calculate fare. Please try again.");
      setFareState("error");
    }
  }, []);

  // Kick off fare fetch immediately when both ends are fully resolved (addr + coords).
  // Airport addresses are constants and known-resolved without coords.
  const maybeFetchFare = useCallback((pickup, drop, pickupCoords, dropCoords, time) => {
    const isAirportAddr = (s) => s && s.includes("Kempegowda International Airport");
    const pickupResolved = !!pickup && (isAirportAddr(pickup) || !!pickupCoords);
    const dropResolved = !!drop && (isAirportAddr(drop) || !!dropCoords);
    if (!pickupResolved || !dropResolved) {
      // Not enough info yet — hide fare card entirely
      fareRequestIdRef.current += 1;
      setFareData(null);
      setFareError(null);
      setFareState("idle");
      return;
    }
    fetchFare(pickup, drop, pickupCoords, dropCoords, time);
  }, [fetchFare]);

  const handlePickupSelect = useCallback((addr, coords) => {
    setFormData((prev) => {
      const next = { ...prev, pickup_location: addr, pickup_coords: coords || null };
      maybeFetchFare(addr, next.drop_location, coords || null, next.drop_coords, next.time);
      return next;
    });
  }, [maybeFetchFare]);

  const handleDropSelect = useCallback((addr, coords) => {
    setFormData((prev) => {
      const next = { ...prev, drop_location: addr, drop_coords: coords || null };
      maybeFetchFare(next.pickup_location, addr, next.pickup_coords, coords || null, next.time);
      return next;
    });
  }, [maybeFetchFare]);

  const handleTimeChange = useCallback((time) => {
    setFormData((prev) => {
      const next = { ...prev, time };
      maybeFetchFare(next.pickup_location, next.drop_location, next.pickup_coords, next.drop_coords, time);
      return next;
    });
  }, [maybeFetchFare]);

  // Auto-fill airport address when terminal changes
  const applyAirportAddress = useCallback((dir, term) => {
    const airportAddr = TERMINAL_ADDRESSES[term];
    setFormData((prev) => {
      if (dir === "to_airport") {
        maybeFetchFare(prev.pickup_location, airportAddr, prev.pickup_coords, null, prev.time);
        return { ...prev, drop_location: airportAddr, drop_coords: null };
      } else {
        maybeFetchFare(airportAddr, prev.drop_location, null, prev.drop_coords, prev.time);
        return { ...prev, pickup_location: airportAddr, pickup_coords: null };
      }
    });
  }, [maybeFetchFare]);

  // Apply airport address when ride type becomes airport
  useEffect(() => {
    if (rideType === "airport") {
      const airportAddr = TERMINAL_ADDRESSES[terminal];
      if (airportDirection === "to_airport") {
        setFormData((prev) => ({ ...prev, drop_location: airportAddr, drop_coords: null }));
      } else {
        setFormData((prev) => ({ ...prev, pickup_location: airportAddr, pickup_coords: null }));
      }
    }
  }, [rideType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTerminalChange = useCallback((term) => {
    setTerminal(term);
    applyAirportAddress(airportDirection, term);
  }, [airportDirection, applyAirportAddress]);

  // SWAP: interchange pickup ↔ drop (toggles direction for airport)
  const handleSwap = useCallback(() => {
    const newDir = airportDirection === "to_airport" ? "from_airport" : "to_airport";
    setAirportDirection(newDir);
    setFormData((prev) => {
      const next = {
        ...prev,
        pickup_location: prev.drop_location,
        drop_location: prev.pickup_location,
        pickup_coords: prev.drop_coords,
        drop_coords: prev.pickup_coords,
      };
      maybeFetchFare(next.pickup_location, next.drop_location, next.pickup_coords, next.drop_coords, next.time);
      return next;
    });
  }, [airportDirection, maybeFetchFare]);

  // For city rides: swap pickup and drop
  const handleCitySwap = useCallback(() => {
    setFormData((prev) => {
      const next = {
        ...prev,
        pickup_location: prev.drop_location,
        drop_location: prev.pickup_location,
        pickup_coords: prev.drop_coords,
        drop_coords: prev.pickup_coords,
      };
      maybeFetchFare(next.pickup_location, next.drop_location, next.pickup_coords, next.drop_coords, next.time);
      return next;
    });
  }, [maybeFetchFare]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Defense-in-depth: only accept form submissions that were explicitly
    // triggered by the Request Booking button. This guards against any
    // button nested in the form (e.g. inside LocationPicker overlays) that
    // forgets type="button" and accidentally submits the form.
    const submitter = e.nativeEvent?.submitter;
    if (submitter && submitter.dataset?.testid !== "submit-booking-btn") {
      return;
    }
    if (!rideType) { toast.error("Please select a ride type"); return; }
    if (!formData.pickup_location || !formData.drop_location || !date || !formData.time || !formData.passenger_name || !formData.passenger_phone) {
      toast.error("Please fill in all required fields"); return;
    }
    const phoneClean = (() => {
      let p = formData.passenger_phone.replace(/[\s\-()]/g, '');
      if (p.startsWith('+91')) p = p.slice(3);
      else if (p.startsWith('91') && p.length === 12) p = p.slice(2);
      return p;
    })();
    if (!/^[6-9]\d{9}$/.test(phoneClean)) { toast.error("Please enter a valid 10-digit Indian mobile number"); return; }
    if (!consentChecked) { toast.error("Please agree to the privacy policy to continue"); return; }
    if (fareState !== "ready" || !computedFare) {
      toast.error("Please wait for the fare to finish calculating before booking.");
      return;
    }
    if (turnstileEnabled && !turnstileToken) {
      toast.error("Please complete the captcha verification before booking.");
      return;
    }
    setIsSubmitting(true);
    try {
      const bookingData = {
        ride_type: rideType,
        pickup_location: formData.pickup_location,
        drop_location: formData.drop_location,
        date: format(date, "PPP"),
        time: formData.time,
        passengers: parseInt(formData.passengers) || 1,
        passenger_name: formData.passenger_name,
        passenger_phone: formData.passenger_phone,
        passenger_email: formData.passenger_email || null,
        flight_number: formData.flight_number || null,
        special_requests: formData.special_requests || null,
        // Pass coords so the server can re-derive the fare authoritatively
        // using the same Places-Autocomplete-anchored locations the user saw.
        pickup_lat: formData.pickup_coords?.lat ?? null,
        pickup_lng: formData.pickup_coords?.lng ?? null,
        drop_lat: formData.drop_coords?.lat ?? null,
        drop_lng: formData.drop_coords?.lng ?? null,
        fare_data: computedFare || null,
        turnstile_token: turnstileToken,
      };
      const response = await axios.post(`${API}/bookings`, bookingData);
      onClose();
      navigate(`/booking/${response.data.id}`);
    } catch (err) {
      // On any failure, force-reset the Turnstile widget so the user gets a
      // fresh single-use token on their next attempt.
      setTurnstileToken(null);
      if (turnstileRef.current) turnstileRef.current.reset();
      const msg = err?.response?.data?.detail || "Failed to submit booking. Please try again.";
      toast.error(msg);
    }
    finally { setIsSubmitting(false); }
  };

  const resetForm = () => {
    setFormData({ pickup_location: "", drop_location: "", pickup_coords: null, drop_coords: null, time: "", passengers: "1", passenger_name: "", passenger_phone: "", passenger_email: "", flight_number: "", special_requests: "" });
    setDate(null);
    setFareData(null);
    setFareState("idle");
    setFareError(null);
    setConsentChecked(false);
    setAirportDirection("to_airport");
    setTerminal("T1");
    setShowSpecialRequests(false);
    setRideType(initialRideType || "");
  };

  const handleClose = () => { resetForm(); onClose(); };

  const showRideTypeSelector = !isLocked && !rideType;

  // Derive which field is the "city address" for airport rides
  const cityField = airportDirection === "to_airport" ? "pickup" : "drop";

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" onClick={handleClose} />
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="w-full sm:max-w-[440px] max-h-[92vh] pointer-events-auto"
            >
              <div className="bg-[#111111] border border-white/[0.08] rounded-t-[28px] sm:rounded-[28px] overflow-y-auto max-h-[92vh] px-6 pt-7 pb-6">

                {/* ── Ride Type Selector ── */}
                {showRideTypeSelector ? (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="font-outfit text-[22px] font-extrabold tracking-tight text-white">Book Your Ride</h2>
                      <button
                        type="button"
                        onClick={handleClose} className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/[0.08] flex items-center justify-center text-[#71717a] hover:text-white transition-colors" data-testid="modal-close-btn"><X className="w-[18px] h-[18px]" /></button>
                    </div>
                    <p className="text-[#71717a] text-sm mb-5">Choose your ride type</p>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => { setRideType("airport"); }} className="bg-[#0a0a0a] border border-white/[0.06] rounded-2xl p-6 text-center hover:border-sarathi-primary/40 transition-all group" data-testid="select-airport-ride">
                        <div className="w-14 h-14 bg-sarathi-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-sarathi-primary/20 transition-colors"><Plane className="w-7 h-7 text-sarathi-primary" /></div>
                        <h3 className="text-white font-semibold mb-1">Airport Transfer</h3>
                        <p className="text-[#52525b] text-xs">To/from KIAL</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          // City rides are in Coming Soon mode during beta — redirect
                          // to the waitlist instead of opening the booking form.
                          // Fallback (e.g. for direct testing): allow City Ride flow.
                          if (onCityRideRequested) {
                            onCityRideRequested();
                          } else {
                            setRideType("city");
                          }
                        }} className="bg-[#0a0a0a] border border-white/[0.06] rounded-2xl p-6 text-center hover:border-amber-500/40 transition-all group relative overflow-hidden" data-testid="select-city-ride">
                        <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-500/15 text-amber-400 text-[9px] font-bold tracking-wider border border-amber-500/30 uppercase">
                          Coming Soon
                        </span>
                        <div className="w-14 h-14 bg-sarathi-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-sarathi-primary/20 transition-colors"><Car className="w-7 h-7 text-sarathi-primary" /></div>
                        <h3 className="text-white font-semibold mb-1">City Ride</h3>
                        <p className="text-[#52525b] text-xs">Within Bengaluru</p>
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* ── Header ── */}
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-3">
                        <h2 className="font-outfit text-[22px] font-extrabold tracking-tight text-white">
                          {rideType === "airport" ? "Airport Transfer" : "City Ride"}
                        </h2>
                        {!isLocked && (
                          <button
                            type="button"
                            onClick={() => { setRideType(""); setFareData(null); setFareState("idle"); setFareError(null); }} className="text-[#52525b] text-xs hover:text-white transition-colors underline" data-testid="change-ride-type">Change</button>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleClose} className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/[0.08] flex items-center justify-center text-[#71717a] hover:text-white transition-colors" data-testid="modal-close-btn"><X className="w-[18px] h-[18px]" /></button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">

                      {/* ════════════════════════════════════════
                          AIRPORT: Location Card with Swap
                         ════════════════════════════════════════ */}
                      {rideType === "airport" ? (
                        <div className="relative">
                          <div className="bg-[#0a0a0a] border border-white/[0.06] rounded-2xl overflow-visible">
                            <div className="flex items-center pl-4 pr-16 py-3.5 gap-3">
                              {airportDirection === "to_airport" ? (
                                <>
                                  <MapPin className="w-[18px] h-[18px] text-sarathi-primary shrink-0" />
                                  <button type="button" onClick={() => setLocationPickerOpen("pickup")} className="flex-1 text-left min-w-0" data-testid="input-pickup">
                                    <span className={`text-[14px] block truncate ${formData.pickup_location ? "text-white font-medium" : "text-[#52525b]"}`}>{formData.pickup_location || "Your pickup address"}</span>
                                  </button>
                                </>
                              ) : (
                                <>
                                  <Plane className="w-[18px] h-[18px] text-[#71717a] shrink-0" />
                                  <span className="text-white font-semibold text-sm">KIAL (BLR)</span>
                                  <div className="ml-auto flex gap-[2px] bg-white/[0.05] rounded-lg p-[2px] mr-2">
                                    <button type="button" onClick={() => handleTerminalChange("T1")} className={`px-3.5 py-1.5 rounded-md text-[13px] font-semibold transition-all ${terminal === "T1" ? "bg-white text-[#0a0a0a]" : "text-[#52525b] hover:text-[#a1a1aa]"}`} data-testid="terminal-t1">T1</button>
                                    <button type="button" onClick={() => handleTerminalChange("T2")} className={`px-3.5 py-1.5 rounded-md text-[13px] font-semibold transition-all ${terminal === "T2" ? "bg-white text-[#0a0a0a]" : "text-[#52525b] hover:text-[#a1a1aa]"}`} data-testid="terminal-t2">T2</button>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="relative border-t border-white/[0.05]">
                              <div className="absolute left-[25px] -top-[9px] flex flex-col gap-[3px]">
                                <span className="w-[2px] h-[2px] rounded-full bg-[#3f3f46]" /><span className="w-[2px] h-[2px] rounded-full bg-[#3f3f46]" /><span className="w-[2px] h-[2px] rounded-full bg-[#3f3f46]" />
                              </div>
                            </div>
                            <div className="flex items-center pl-4 pr-16 py-3.5 gap-3">
                              {airportDirection === "to_airport" ? (
                                <>
                                  <Plane className="w-[18px] h-[18px] text-[#71717a] shrink-0" />
                                  <span className="text-white font-semibold text-sm">KIAL (BLR)</span>
                                  <div className="ml-auto flex gap-[2px] bg-white/[0.05] rounded-lg p-[2px] mr-2">
                                    <button type="button" onClick={() => handleTerminalChange("T1")} className={`px-3.5 py-1.5 rounded-md text-[13px] font-semibold transition-all ${terminal === "T1" ? "bg-white text-[#0a0a0a]" : "text-[#52525b] hover:text-[#a1a1aa]"}`} data-testid="terminal-t1">T1</button>
                                    <button type="button" onClick={() => handleTerminalChange("T2")} className={`px-3.5 py-1.5 rounded-md text-[13px] font-semibold transition-all ${terminal === "T2" ? "bg-white text-[#0a0a0a]" : "text-[#52525b] hover:text-[#a1a1aa]"}`} data-testid="terminal-t2">T2</button>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <MapPin className="w-[18px] h-[18px] text-sarathi-primary shrink-0" />
                                  <button type="button" onClick={() => setLocationPickerOpen("drop")} className="flex-1 text-left min-w-0" data-testid="input-drop">
                                    <span className={`text-[14px] block truncate ${formData.drop_location && !formData.drop_location.includes("Kempegowda") ? "text-white font-medium" : "text-[#52525b]"}`}>{formData.drop_location && !formData.drop_location.includes("Kempegowda") ? formData.drop_location : "Your drop address"}</span>
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          <button type="button" onClick={handleSwap} className="absolute -right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#1a1a1a] border-2 border-[#27272a] flex items-center justify-center hover:border-sarathi-primary hover:bg-sarathi-primary/10 transition-all z-10" data-testid="swap-direction-btn" title="Swap direction">
                            <ArrowUpDown className="w-4 h-4 text-[#a1a1aa]" />
                          </button>
                        </div>
                      ) : (
                        <div className="relative">
                          <div className="bg-[#0a0a0a] border border-white/[0.06] rounded-2xl overflow-visible">
                            <div className="flex items-center pl-4 pr-16 py-3.5 gap-3">
                              <MapPin className="w-[18px] h-[18px] text-sarathi-primary shrink-0" />
                              <button type="button" onClick={() => setLocationPickerOpen("pickup")} className="flex-1 text-left min-w-0" data-testid="input-pickup">
                                <span className={`text-[14px] block truncate ${formData.pickup_location ? "text-white font-medium" : "text-[#52525b]"}`}>{formData.pickup_location || "Pickup address"}</span>
                              </button>
                            </div>
                            <div className="relative border-t border-white/[0.05]">
                              <div className="absolute left-[25px] -top-[9px] flex flex-col gap-[3px]">
                                <span className="w-[2px] h-[2px] rounded-full bg-[#3f3f46]" /><span className="w-[2px] h-[2px] rounded-full bg-[#3f3f46]" /><span className="w-[2px] h-[2px] rounded-full bg-[#3f3f46]" />
                              </div>
                            </div>
                            <div className="flex items-center pl-4 pr-16 py-3.5 gap-3">
                              <MapPin className="w-[18px] h-[18px] text-[#71717a] shrink-0" />
                              <button type="button" onClick={() => setLocationPickerOpen("drop")} className="flex-1 text-left min-w-0" data-testid="input-drop">
                                <span className={`text-[14px] block truncate ${formData.drop_location ? "text-white font-medium" : "text-[#52525b]"}`}>{formData.drop_location || "Drop-off address"}</span>
                              </button>
                            </div>
                          </div>
                          <button type="button" onClick={handleCitySwap} className="absolute -right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#1a1a1a] border-2 border-[#27272a] flex items-center justify-center hover:border-sarathi-primary hover:bg-sarathi-primary/10 transition-all z-10" data-testid="swap-direction-btn" title="Swap pickup & drop">
                            <ArrowUpDown className="w-4 h-4 text-[#a1a1aa]" />
                          </button>
                        </div>
                      )}

                      {/* Location Picker Overlay */}
                      <LocationPicker
                        isOpen={locationPickerOpen !== null}
                        onClose={() => setLocationPickerOpen(null)}
                        onConfirm={(addr, coords) => {
                          if (locationPickerOpen === "pickup") handlePickupSelect(addr, coords);
                          else handleDropSelect(addr, coords);
                        }}
                        label={locationPickerOpen === "pickup" ? "Pick-up Location" : "Drop Location"}
                      />

                      {/* ── Date & Time ── */}
                      <div className="grid grid-cols-2 gap-3">
                        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                          <PopoverTrigger asChild>
                            <button type="button" className="flex items-center gap-2.5 bg-[#0a0a0a] border border-white/[0.06] rounded-[14px] px-4 py-3.5 w-full text-left" data-testid="date-picker-trigger">
                              <CalendarIcon className="w-4 h-4 text-[#71717a] shrink-0" />
                              <span className={`text-[13px] font-medium ${date ? "text-white" : "text-[#52525b]"}`}>{date ? format(date, "MMM d, yyyy") : "Pick date"}</span>
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-[#111111] border-white/10" align="start">
                            <Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setCalendarOpen(false); }} disabled={(d) => { const today = new Date(); today.setHours(0,0,0,0); return d < today; }} initialFocus className="bg-[#111111] text-white" data-testid="calendar" />
                          </PopoverContent>
                        </Popover>

                        <Select value={formData.time} onValueChange={handleTimeChange}>
                          <SelectTrigger className="bg-[#0a0a0a] border-white/[0.06] text-white rounded-[14px] h-auto py-3.5 px-4" data-testid="time-picker">
                            <div className="flex items-center gap-2.5">
                              <Clock className="w-4 h-4 text-[#71717a]" />
                              <span className={`text-[13px] font-medium ${formData.time ? "text-white" : "text-[#52525b]"}`}>{formData.time || "Pick time"}</span>
                            </div>
                          </SelectTrigger>
                          <SelectContent className="bg-[#111111] border-white/10 max-h-60">
                            {getAvailableTimeSlots(date).map((t) => (<SelectItem key={t} value={t} className="text-white hover:bg-white/5">{t}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* ── Flight details (airport only) ── */}
                      {rideType === "airport" && (
                        <>
                          <p className="text-[11px] font-bold tracking-[1.5px] uppercase text-[#71717a] mt-1">Flight Details</p>
                          <div className="flex items-center gap-2.5 bg-[#0a0a0a] border border-white/[0.06] rounded-[14px] px-4 py-3.5">
                            <PlaneTakeoff className="w-4 h-4 text-[#71717a] shrink-0" />
                            <input name="flight_number" value={formData.flight_number} onChange={handleInputChange}
                              placeholder="E.g. — 6E242 (optional)" className="bg-transparent text-[13px] text-white placeholder:text-[#52525b] w-full outline-none" data-testid="input-flight" />
                          </div>
                        </>
                      )}

                      {/* ── Passengers + Special Requests pills ── */}
                      <div className="flex gap-3">
                        <Select value={formData.passengers} onValueChange={(v) => setFormData(prev => ({ ...prev, passengers: v }))}>
                          <SelectTrigger className="bg-transparent border border-white/10 rounded-[14px] h-auto py-3 px-4 flex-1" data-testid="passengers-picker">
                            <span className="text-[#a1a1aa] text-[13px] font-semibold">{formData.passengers} {parseInt(formData.passengers) === 1 ? "Passenger" : "Passengers"}</span>
                          </SelectTrigger>
                          <SelectContent className="bg-[#111111] border-white/10">
                            {[1, 2, 3, 4].map((n) => (<SelectItem key={n} value={String(n)} className="text-white hover:bg-white/5">{n} {n === 1 ? "Passenger" : "Passengers"}</SelectItem>))}
                          </SelectContent>
                        </Select>
                        <button type="button" onClick={() => setShowSpecialRequests(!showSpecialRequests)}
                          className={`flex-1 py-3 rounded-[14px] border text-[13px] font-semibold transition-all ${showSpecialRequests ? "border-sarathi-primary/50 text-sarathi-primary bg-sarathi-primary/5" : "border-white/10 text-[#a1a1aa] hover:border-white/20"}`}
                          data-testid="toggle-special-requests">
                          Special Requests
                        </button>
                      </div>

                      {/* Special requests expandable */}
                      <AnimatePresence>
                        {showSpecialRequests && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                            <textarea name="special_requests" value={formData.special_requests} onChange={handleInputChange}
                              placeholder="E.g. child seat, extra luggage, wheelchair access" rows={2}
                              className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/[0.06] text-white placeholder:text-[#52525b] rounded-[14px] resize-none outline-none focus:ring-1 focus:ring-sarathi-primary text-[13px]" data-testid="input-special-requests" />
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* ── Passenger details ── */}
                      <p className="text-[11px] font-bold tracking-[1.5px] uppercase text-[#71717a]">Passenger</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center gap-2.5 bg-[#0a0a0a] border border-white/[0.06] rounded-[14px] px-4 py-3.5">
                          <User className="w-4 h-4 text-[#71717a] shrink-0" />
                          <input name="passenger_name" value={formData.passenger_name} onChange={handleInputChange} placeholder="Full Name" className="bg-transparent text-[13px] text-white placeholder:text-[#52525b] w-full outline-none" data-testid="input-name" />
                        </div>
                        <div className="flex items-center gap-2.5 bg-[#0a0a0a] border border-white/[0.06] rounded-[14px] px-4 py-3.5">
                          <Phone className="w-4 h-4 text-[#71717a] shrink-0" />
                          <input name="passenger_phone" value={formData.passenger_phone} onChange={handleInputChange} placeholder="Phone" className="bg-transparent text-[13px] text-white placeholder:text-[#52525b] w-full outline-none" data-testid="input-phone" />
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5 bg-[#0a0a0a] border border-white/[0.06] rounded-[14px] px-4 py-3.5">
                        <Mail className="w-4 h-4 text-[#71717a] shrink-0" />
                        <input name="passenger_email" value={formData.passenger_email} onChange={handleInputChange} type="email" placeholder="Email (optional — for receipt)" className="bg-transparent text-[13px] text-white placeholder:text-[#52525b] w-full outline-none" data-testid="input-email" />
                      </div>

                      {/* ── Fare card (4 states: idle hidden | calculating | ready | error) ── */}
                      {fareState === "calculating" && (
                        <div className="bg-[rgba(249,115,22,0.04)] border border-[rgba(249,115,22,0.12)] rounded-2xl p-4 flex items-center gap-3" data-testid="fare-calculating">
                          <Loader2 className="w-4 h-4 animate-spin text-sarathi-primary shrink-0" />
                          <div className="flex-1">
                            <p className="text-white text-sm font-medium">
                              {fareData ? "Recalculating fare…" : "Calculating fare…"}
                            </p>
                            <p className="text-[#71717a] text-[11px] mt-0.5">Locking in your fixed fare for this pickup & drop</p>
                          </div>
                        </div>
                      )}
                      {fareState === "ready" && fareData?.success && computedFare && (
                        <FareBreakdown fareData={fareData} computedFare={computedFare} />
                      )}
                      {fareState === "error" && (
                        <div className="bg-[#0a0a0a] rounded-2xl p-4 border border-red-500/20 text-center" data-testid="fare-error">
                          <p className="text-red-400 text-sm font-medium">{fareError || "Unable to calculate fare"}</p>
                          {fareData?.fallback && <p className="text-[#71717a] text-xs mt-1">{fareData.fallback}</p>}
                          <p className="text-[#52525b] text-[11px] mt-2">Please re-select your pickup and drop from the suggestions.</p>
                        </div>
                      )}

                      {/* ── Policy strip ── */}
                      <div className="bg-[rgba(249,115,22,0.04)] border border-[rgba(249,115,22,0.1)] rounded-[14px] px-4 py-3.5 flex flex-col gap-2">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-lg bg-white/[0.05] flex items-center justify-center shrink-0">
                            <Clock className="w-3.5 h-3.5 text-[#71717a]" />
                          </div>
                          <p className="text-[12px] text-[#a1a1aa]"><strong className="text-[#d4d4d8] font-semibold">Free waiting — 30 mins</strong> at airport</p>
                        </div>
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-lg bg-white/[0.05] flex items-center justify-center shrink-0">
                            <X className="w-3.5 h-3.5 text-[#71717a]" />
                          </div>
                          <p className="text-[12px] text-[#a1a1aa]"><strong className="text-[#d4d4d8] font-semibold">Free cancellation</strong> up to 3 hrs before scheduled pickup time</p>
                        </div>
                      </div>

                      {/* ── Consent ── */}
                      <div className="flex items-start gap-3">
                        <Checkbox id="privacy-consent" checked={consentChecked} onCheckedChange={(c) => setConsentChecked(c === true)}
                          className="mt-0.5 border-white/[0.15] data-[state=checked]:bg-sarathi-primary data-[state=checked]:border-sarathi-primary" data-testid="privacy-consent-checkbox" />
                        <label htmlFor="privacy-consent" className="text-[#71717a] text-[11px] leading-relaxed cursor-pointer">
                          I agree to Sarathi-X's{" "}<a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-sarathi-primary hover:underline" data-testid="privacy-policy-link">Privacy Policy</a>{" "}and{" "}<a href="/terms" target="_blank" rel="noopener noreferrer" className="text-sarathi-primary hover:underline">Terms</a>
                        </label>
                      </div>

                      {/* ── Turnstile (Cloudflare bot challenge) ── */}
                      {turnstileEnabled && turnstileSiteKey ? (
                        <div className="flex justify-center" data-testid="turnstile-container">
                          <TurnstileWidget
                            siteKey={turnstileSiteKey}
                            theme="dark"
                            size="flexible"
                            onToken={setTurnstileToken}
                            widgetRef={turnstileRef}
                          />
                        </div>
                      ) : null}

                      {/* ── CTA ── */}
                      <button type="submit" disabled={isSubmitting || !consentChecked || fareState !== "ready" || !configLoaded || (turnstileEnabled && !turnstileToken)}
                        className="w-full py-4 rounded-2xl bg-sarathi-primary hover:bg-orange-600 text-white font-bold text-[16px] tracking-tight disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-[0_4px_24px_rgba(249,115,22,0.25)]"
                        data-testid="submit-booking-btn">
                        {isSubmitting ? (
                          <span className="flex items-center justify-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Submitting...</span>
                        ) : fareState === "calculating" ? (
                          <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Waiting for fare…</span>
                        ) : fareState === "error" ? (
                          <span className="flex items-center justify-center gap-2">Fix address to continue</span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">Request Booking<ArrowRight className="w-5 h-5" /></span>
                        )}
                      </button>
                      <p className="text-[#52525b] text-[11px] text-center">Confirmed on WhatsApp within 30 min</p>
                    </form>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BookingModal;
